import { Fragment, useMemo } from 'react';
import type { PtoPprFinding, PtoPprTable, PtoPprTkDraft } from '@shared/api';
import { Muted, TableWrap, Tag } from '../kfo/ui';
import {
  ORIGIN_LABEL,
  PLACEHOLDER_RE,
  SEVERITY_LABEL,
  SEVERITY_TONE,
  citationKind,
  citationShortLabel,
  citationTitle,
  humanizeRefs,
  sectionLabelByKey,
  toBlocks,
} from './model';
import {
  Bullets,
  Chip,
  ChipRow,
  FindingBox,
  FindingHead,
  LinkButton,
  Paragraph,
  Placeholder,
  PprDataTable,
  TableCaption,
} from './ui';

/** Строка с подсвеченными заглушками «[уточнить: …]». */
function Marked({ text }: { text: string }) {
  const parts = text.split(PLACEHOLDER_RE);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? <Placeholder key={i}>{p}</Placeholder> : <Fragment key={i}>{p}</Fragment>,
      )}
    </>
  );
}

/** Текст раздела: `\n` — абзац, «- » — пункт списка. */
export function SectionText({ content }: { content: string }) {
  const blocks = useMemo(() => toBlocks(content), [content]);
  return (
    <>
      {blocks.map((b, i) =>
        b.kind === 'p' ? (
          <Paragraph key={i}>
            <Marked text={b.text} />
          </Paragraph>
        ) : (
          <Bullets key={i}>
            {b.items.map((item, k) => (
              <li key={k}>
                <Marked text={item} />
              </li>
            ))}
          </Bullets>
        ),
      )}
    </>
  );
}

/** Таблица раздела (собрана кодом из данных — графы формы ППР-004, объёмы, нормативы). */
export function PprTableView({ table }: { table: PtoPprTable }) {
  return (
    <div>
      {table.caption && <TableCaption>{table.caption}</TableCaption>}
      <TableWrap $maxHeight="none">
        <PprDataTable>
          <thead>
            <tr>
              {table.columns.map((c, i) => (
                <th key={i}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, k) => (
                  <td key={k}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </PprDataTable>
      </TableWrap>
      {table.note && <Muted>{table.note}</Muted>}
    </div>
  );
}

const KIND_SHORT: Record<string, string> = {
  rd: 'РД',
  vol: 'объём',
  eq: 'техника',
  calc: 'расчёт',
  std: 'норматив',
  in: 'исх.',
};

/** Чипы ссылок на исходные данные; подсказка — сведение с цитатой и листом РД и т.п. */
export function CitationChips({
  items,
  draft,
}: {
  items: { id: string; label: string }[];
  draft: PtoPprTkDraft;
}) {
  if (items.length === 0) return null;
  return (
    <ChipRow>
      {items.map((c, i) => {
        const kind = citationKind(c.id);
        return (
          <Chip
            key={`${c.id}-${i}`}
            $kind={kind}
            tabIndex={0}
            data-cite={c.id}
            title={citationTitle(c.id, c.label, draft)}
          >
            <b>{KIND_SHORT[kind] ?? kind}</b>
            {c.label}
          </Chip>
        );
      })}
    </ChipRow>
  );
}

/**
 * Замечание агента-аудитора или проверки кода. `onJump` — переход к разделу
 * (во вкладке «Аудит»); в самом разделе ссылка не нужна.
 */
export function FindingItem({
  finding: f,
  draft,
  onJump,
  sectionKeys,
}: {
  finding: PtoPprFinding;
  draft: PtoPprTkDraft;
  onJump?: (key: string) => void;
  /** Разделы склеенного замечания (по умолчанию — раздел самого замечания). */
  sectionKeys?: string[];
}) {
  const refs = f.refs.map((id) => ({ id, label: citationShortLabel(id, draft) }));
  const keys = sectionKeys ?? (f.section_key ? [f.section_key] : []);
  return (
    <FindingBox $tone={SEVERITY_TONE[f.severity] ?? 'neutral'} data-finding={f.severity}>
      <FindingHead>
        <Tag $tone={SEVERITY_TONE[f.severity] ?? 'neutral'}>
          {SEVERITY_LABEL[f.severity] ?? f.severity}
        </Tag>
        <Muted as="span">{ORIGIN_LABEL[f.origin] ?? f.origin}</Muted>
        {onJump &&
          keys.map((k) => (
            <LinkButton key={k} type="button" onClick={() => onJump(k)}>
              {sectionLabelByKey(draft, k)}
            </LinkButton>
          ))}
        {onJump && keys.length === 0 && <Muted as="span">весь документ</Muted>}
        {!f.fixable && <Tag $tone="neutral">нужны данные/решение инженера</Tag>}
      </FindingHead>
      <div>{humanizeRefs(f.message, draft)}</div>
      {f.fix && (
        <div>
          <b>Исправить:</b> {humanizeRefs(f.fix, draft)}
        </div>
      )}
      <CitationChips items={refs} draft={draft} />
    </FindingBox>
  );
}
